import { Component, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../core/environment';

interface DashboardSummary {
  totalStudents: number;
  activeStudents: number;
  todayGuestPlayers: number;
  summerCampStudents: number;
  todayAttendance: number;
  pendingFees: number;
  staffPresentToday: number;
}

interface MetricCard {
  label: string;
  value: string | number;
  note: string;
  tone: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [RouterLink],
  template: `
    <div class="dashboard-page">
      <section class="dashboard-header">
        <div>
          <span class="eyebrow">Academy command center</span>
          <h2>Dashboard Overview</h2>
          <p>Admissions, guest play, attendance and fees in one clean operating view.</p>
        </div>
        <div class="dashboard-date">
          <span>Today</span>
          <strong>{{ today }}</strong>
        </div>
      </section>

      <section class="dashboard-kpis">
        @for (card of cards(); track card.label) {
          <article class="kpi-card {{ card.tone }}">
            <span>{{ card.label }}</span>
            <strong>{{ card.value }}</strong>
            <p>{{ card.note }}</p>
          </article>
        }
      </section>

      <section class="dashboard-grid">
        <article class="dashboard-panel attendance-panel">
          <div class="panel-heading">
            <div>
              <h3>Recent Attendance</h3>
              <p>Live check-in activity from today.</p>
            </div>
            <a routerLink="/attendance">View logs</a>
          </div>

          <div class="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Sport</th>
                  <th>In-Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                @for (row of attendanceRows; track row.name) {
                  <tr>
                    <td>
                      <div class="member-cell">
                        <span>{{ row.initials }}</span>
                        <strong>{{ row.name }}</strong>
                      </div>
                    </td>
                    <td>{{ row.sport }}</td>
                    <td>{{ row.time }}</td>
                    <td><span class="status-chip {{ row.statusClass }}">{{ row.status }}</span></td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </article>

        <aside class="dashboard-side">
          <article class="quick-actions">
            <div class="panel-heading">
              <div>
                <h3>Quick Actions</h3>
                <p>Front desk shortcuts.</p>
              </div>
            </div>
            <a routerLink="/students">Add New Student</a>
            <a routerLink="/guest-booking">Guest Check-In</a>
            <a routerLink="/qr-cards">Generate QR Card</a>
          </article>

          <article class="dashboard-panel">
            <div class="panel-heading">
              <div>
                <h3>Weekly Revenue</h3>
                <p>Collection by sport.</p>
              </div>
              <span class="mini-badge">This week</span>
            </div>
            <div class="revenue-list">
              @for (item of revenueRows; track item.label) {
                <div>
                  <div>
                    <span>{{ item.label }}</span>
                    <strong>{{ item.value }}</strong>
                  </div>
                  <span class="progress-track"><span [style.width]="item.width"></span></span>
                </div>
              }
            </div>
          </article>
        </aside>
      </section>

      <section class="event-banner">
        <div>
          <span>Upcoming Event</span>
          <h3>Summer Championship Registration is Open</h3>
          <p>Coordinate trial batches, trainers and payments from the summer camp module.</p>
        </div>
        <a routerLink="/summer-camp">Open Camp Desk</a>
      </section>
    </div>
  `
})
export class DashboardComponent implements OnInit {
  readonly today = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: '2-digit', month: 'short' }).format(new Date());
  readonly summary = signal<DashboardSummary | null>(null);
  readonly cards = signal<MetricCard[]>(this.buildCards(null));

  readonly attendanceRows = [
    { initials: 'JS', name: 'Jordon Smith', sport: 'Cricket', time: '04:15 PM', status: 'Check-In', statusClass: 'success' },
    { initials: 'AM', name: 'Alisha Miller', sport: 'Swimming', time: '04:30 PM', status: 'Check-In', statusClass: 'success' },
    { initials: 'RR', name: 'Rahul Roy', sport: 'Tennis', time: '04:45 PM', status: 'Trial Play', statusClass: 'info' },
    { initials: 'ED', name: 'Elena Drake', sport: 'Badminton', time: '05:00 PM', status: 'Late Entry', statusClass: 'warning' }
  ];

  readonly revenueRows = [
    { label: 'Cricket', value: 'INR 4,200', width: '85%' },
    { label: 'Swimming', value: 'INR 3,150', width: '65%' },
    { label: 'Tennis', value: 'INR 2,800', width: '55%' },
    { label: 'Others', value: 'INR 1,400', width: '30%' }
  ];

  constructor(private readonly http: HttpClient) {}

  ngOnInit(): void {
    this.http.get<DashboardSummary>(`${environment.apiUrl}/dashboard/summary`).subscribe({
      next: (summary) => {
        this.summary.set(summary);
        this.cards.set(this.buildCards(summary));
      },
      error: () => this.cards.set(this.buildCards(null))
    });
  }

  private buildCards(summary: DashboardSummary | null): MetricCard[] {
    return [
      {
        label: 'Total Students',
        value: summary?.totalStudents || '1,284',
        note: `${summary?.activeStudents || 1150} active students`,
        tone: 'tone-green'
      },
      {
        label: 'Guest Players',
        value: summary?.todayGuestPlayers || 42,
        note: 'Today walk-in and hourly play',
        tone: 'tone-blue'
      },
      {
        label: 'Camp Enrolments',
        value: summary?.summerCampStudents || 186,
        note: 'Summer camp registrations',
        tone: 'tone-amber'
      },
      {
        label: 'Pending Fees',
        value: `INR ${summary?.pendingFees || '12,450'}`,
        note: 'Collections requiring follow-up',
        tone: 'tone-red'
      },
      {
        label: 'Staff Present',
        value: `${summary?.staffPresentToday || 24}/28`,
        note: `${summary?.todayAttendance || 76} attendance logs today`,
        tone: 'tone-slate'
      }
    ];
  }
}
